<div class="inventory-container" *transloco="let t; prefix: 'parts.inventory'">
  <div class="inventory-main">
    <!-- filters -->
    <div
      class="filter-container pb-3 d-flex w-100 justify-content-between gap-2">
      <div class="search-wrapper">
        <app-search
          [placeholder]="t('search.placeholder')"
          [disabled]="isLoading"
          (searchTermUpdated)="onSearchTermUpdate($event)"
          customWidth="396px">
        </app-search>
      </div>

      <div class="filters-wrapper d-flex gap-2">
        <!-- Warehouse Filter -->
        <span ngbDropdown class="filter-dropdown" [autoClose]="true">
          <button
            type="button"
            class="btn warehouse-filter form-control dropdown-toggle filter-input"
            [disabled]="isLoading || isWarehousesLoading"
            id="warehouseFilterDropdown"
            ngbDropdownToggle>
            <div
              class="d-flex flex-row justify-content-between align-items-center">
              <span
                class="align-middle selected-filter-label"
                [ngbPopover]="getSelectedWarehouseTooltip()"
                triggers="mouseenter:mouseleave"
                placement="bottom"
                popoverClass="warehouse-name-popover"
                container="body">
                {{ selectedWarehouseLabel }}
              </span>
              <i class="ph ph-caret-down lh-base"></i>
            </div>
          </button>
          <div
            ngbDropdownMenu
            aria-labelledby="warehouseFilterDropdown"
            class="dropdown-menu-custom warehouse-dropdown-menu">
            @if (shouldShowWarehouseSearch) {
              <div class="dropdown-search">
                <input
                  type="text"
                  class="form-control warehouse-search-input"
                  [placeholder]="t('filters.searchWarehouse')"
                  [value]="warehouseSearchTerm"
                  (input)="warehouseSearchTerm = $any($event.target).value"
                  (click)="$event.stopPropagation()" />
              </div>
            }
            @for (warehouse of filteredWarehouseOptions; track warehouse.id) {
              <button
                class="btn btn-outline dropdown-item-custom"
                (click)="onWarehouseFilterChange(warehouse)"
                [ngbPopover]="getWarehouseItemTooltip(warehouse.name)"
                triggers="mouseenter:mouseleave"
                placement="auto"
                popoverClass="warehouse-name-popover"
                container="body"
                ngbDropdownItem>
                <span class="dropdown-item-label">{{ warehouse.name }}</span>
              </button>
            }
          </div>
        </span>

        <!-- Category Filter -->
        <span ngbDropdown class="filter-dropdown" [autoClose]="true">
          <button
            type="button"
            class="btn category-filter form-control dropdown-toggle filter-input"
            [disabled]="isLoading || isCategoriesLoading"
            id="categoryFilterDropdown"
            ngbDropdownToggle>
            <div
              class="d-flex flex-row justify-content-between align-items-center">
              <span class="align-middle selected-filter-label">{{ selectedCategoryLabel }}</span>
              <i class="ph ph-caret-down lh-base"></i>
            </div>
          </button>
          <div
            ngbDropdownMenu
            aria-labelledby="categoryFilterDropdown"
            class="dropdown-menu-custom category-dropdown-menu">
            @for (category of categoryOptions; track category.id) {
              <button
                class="btn btn-outline dropdown-item-custom"
                (click)="onCategoryFilterChange(category)"
                ngbDropdownItem>
                {{ category.name }}
              </button>
            }
          </div>
        </span>

        <!-- Stock Status Filter -->
        <span ngbDropdown class="filter-dropdown" [autoClose]="true">
          <button
            type="button"
            class="btn stock-status-filter form-control dropdown-toggle filter-input"
            [disabled]="isLoading"
            id="stockStatusFilterDropdown"
            ngbDropdownToggle>
            <div
              class="d-flex flex-row justify-content-between align-items-center">
              <span class="align-middle selected-filter-label">{{ selectedStockStatusLabel }}</span>
              <i class="ph ph-caret-down lh-base"></i>
            </div>
          </button>
          <div
            ngbDropdownMenu
            aria-labelledby="stockStatusFilterDropdown"
            class="dropdown-menu-custom stock-status-dropdown-menu">
            @for (option of stockStatusOptions; track option.id) {
              <button
                class="btn btn-outline dropdown-item-custom"
                (click)="onStockStatusFilterChange(option)"
                ngbDropdownItem>
                {{ option.name }}
              </button>
            }
          </div>
        </span>
      </div>
    </div>

    <!-- Table -->
    <div class="main-table inventory-overview">
      <table class="table">
        <thead>
          <tr>
            <th class="oem-parts-number" appSortHeader="partNumber" [active]="sortColumn" [direction]="sortDirection" (sort)="onSort($event)">{{ t('table.partsNumber') }}</th>
            <th class="part-name" appSortHeader="partName" [active]="sortColumn" [direction]="sortDirection" (sort)="onSort($event)">{{ t('table.partName') }}</th>
            <th class="category" appSortHeader="category" [active]="sortColumn" [direction]="sortDirection" (sort)="onSort($event)">{{ t('table.category') }}</th>
            <th class="on-hand" appSortHeader="onHand" [active]="sortColumn" [direction]="sortDirection" (sort)="onSort($event)">{{ t('table.onHand') }}</th>
            <th class="reorder-point" appSortHeader="reorderPoint" [active]="sortColumn" [direction]="sortDirection" (sort)="onSort($event)">{{ t('table.reorderPoint') }}</th>
            <th class="location" appSortHeader="location" [active]="sortColumn" [direction]="sortDirection" (sort)="onSort($event)">{{ t('table.location') }}</th>
            <th class="actions">{{ t('table.actions') }}</th>
          </tr>
        </thead>
        @if (isLoading && !displayedItems.length) {
          <tbody>
            <tr class="loading-row">
              <td colspan="7">
                <div class="spinner-container">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">{{ t('table.loading') }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        }

        @if (!isLoading || displayedItems.length) {
          <tbody
            infiniteScroll
            [scrollWindow]="false"
            (scrolled)="onScrollDown()"
            [infiniteScrollDistance]="2"
            [infiniteScrollThrottle]="300"
            (scroll)="onTableScroll()">
            @if (
              hasInitialLoadCompleted && !displayedItems.length && !isLoading
            ) {
              <span class="table__no-data">
                <app-empty-state></app-empty-state>
              </span>
            }

            @for (item of displayedItems; track item.partId) {
              <tr class="inventory-row" (click)="openPartOverview(item)">
                <td class="oem-parts-number">
                  <p
                    class="part-number-text"
                    [ngbPopover]="getPartNumberTooltip(item.partNumber)"
                    triggers="mouseenter:mouseleave"
                    placement="auto"
                    popoverClass="warehouse-name-popover"
                    container="body">
                    {{ item.partNumber }}
                  </p>
                </td>
                <td class="part-name">
                  <button
                    type="button"
                    class="part-name-button"
                    [ngbPopover]="getPartNameTooltip(item)"
                    triggers="mouseenter:mouseleave"
                    placement="auto"
                    popoverClass="warehouse-name-popover"
                    container="body">
                    <span class="part-name-text">{{ item.partName }}</span>
                  </button>
                </td>
                <td class="category">
                  <p>{{ item.category }}</p>
                </td>
                <td class="on-hand">
                  <p [ngClass]="{ 'low-stock': isReorderPointReached(item) }">
                    {{ item.onHand }}
                  </p>
                </td>
                <td class="reorder-point">
                  <p
                    [ngClass]="{
                      'reorder-warning': isReorderPointReached(item),
                    }">
                    {{ t('table.min') }}
                    <span class="reorder-value">{{ item.reorderPoint }}</span>
                  </p>
                </td>
                <td class="location">
                  <p
                    class="location-text"
                    [ngbPopover]="getLocationTooltip(item.location)"
                    triggers="mouseenter:mouseleave"
                    placement="auto"
                    popoverClass="warehouse-name-popover"
                    container="body">
                    {{ item.location || '-' }}
                  </p>
                </td>
                <td class="actions" (click)="$event.stopPropagation()">
                  <div
                    ngbDropdown
                    class="action-dropdown"
                    container="body"
                    placement="auto">
                    <button
                      class="btn btn-link action-btn"
                      ngbDropdownToggle
                      [attr.aria-label]="t('table.actionsFor', { name: item.partName })">
                      <i class="ph ph-dots-three-vertical"></i>
                    </button>
                    <div ngbDropdownMenu class="dropdown-menu inventory-actions-menu">
                      <button
                        ngbDropdownItem
                        (click)="onEditClick(item, $event)">
                        {{ t('actions.edit') }}
                      </button>
                      <button
                        ngbDropdownItem
                        (click)="onAddStockClick(item, $event)">
                        {{ t('actions.addStock') }}
                      </button>
                      <button
                        ngbDropdownItem
                        class="text-danger"
                        (click)="onDeleteClick(item, $event)">
                        {{ t('actions.delete') }}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            }

            @if (isLoadingMore) {
              <tr>
                <td colspan="7">
                  <div class="d-flex justify-content-center">
                    <span class="spinner-border text-light"></span>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        }
      </table>
    </div>
  </div>

  <aside class="inventory-sidebar">
    <app-usage-forecast
      [accountIds]="accountIds"
      [accountsLoaded]="accountsLoaded">
    </app-usage-forecast>
  </aside>
</div>